import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { i18next } from '@frontend/i18n';
import { findPmsTypeFromName } from '@frontend/payments-hooks';
import { default as OnlineBillPaySvg } from '../assets/online-bill-pay.svg';
import { default as PaymentsSvg } from '../assets/payments.svg';
import { default as TextToPaySvg } from '../assets/text-to-pay.svg';
import { CommonActionTrackingIds } from './tracking-ids';
import { FeatureContentType, PaymentActionEnum, PaymentActionType } from './types';

export const PaymentsFeatureContentList: FeatureContentType[] = [
  {
    title: i18next.t('63% of patients are more likely to pay quickly using Text-to-Pay.', {
      ns: 'highValueAdoption>>payments',
    }),
    image: OnlineBillPaySvg,
  },
  {
    title: i18next.t('Save over 2 hours per day using Weave Text-to-Pay.', { ns: 'highValueAdoption>>payments' }),
    image: PaymentsSvg,
  },
  {
    title: i18next.t('83% of offices that use Weave Payments collect faster.', { ns: 'highValueAdoption>>payments' }),
    image: TextToPaySvg,
  },
];

export const PaymentActionsList: PaymentActionType[] = [
  {
    type: PaymentActionEnum.CREATE_INVOICE,
    title: i18next.t('Create a payment request', { ns: 'highValueAdoption>>payments' }),
    iconName: 'in-progress',
    trackingId: 'payment-action-create-invoice',
  },
  {
    type: PaymentActionEnum.ONLINE_BILL_PAY,
    title: i18next.t('Get free Online Bill Pay link', { ns: 'highValueAdoption>>payments' }),
    iconName: 'in-progress',
    trackingId: 'payment-action-online-bill-pay',
  },
  {
    type: PaymentActionEnum.SHARE_INVOICE,
    title: i18next.t('Send Text-to-Pay link to customers', { ns: 'highValueAdoption>>payments' }),
    iconName: 'in-progress',
    trackingId: 'payment-action-share-invoice',
  },
];

export const PAYMENTS_HVA_FF = 'high-value-adoption:payments';
export const PAYMENTS_APPOINMENT_PROMO_FF = 'high-value-adoption:payments:appointments';

export const PAYMENT_ACCOUNT_HELP_LINK = 'https://www.weavehelp.com/weavehelp/categories/Payments';
export const ONLINE_BILL_PAY_ROUTE = '/payments/online-bill-pay';
export const SHARE_INVOICE_ROUTE = '/payments/invoices';

export const FeatureNotificationActionDataMap: Partial<
  Record<PlatformLocationFeatureV1.Types.Action, { label: string; trackingId: string }>
> = {
  [PlatformLocationFeatureV1.Types.Action.ACTION_REMIND_AFTER_3_DAYS]: {
    label: i18next.t('Snooze for 3 days', {
      ns: 'highValueAdoption>>payments',
    }),
    trackingId: CommonActionTrackingIds.REMIND_IN_3_DAYS,
  },
  [PlatformLocationFeatureV1.Types.Action.ACTION_NEVER_SHOW_AGAIN]: {
    label: i18next.t('Never show again', {
      ns: 'highValueAdoption>>payments',
    }),
    trackingId: CommonActionTrackingIds.NEVER_SHOW_AGAIN,
  },
  [PlatformLocationFeatureV1.Types.Action.ACTION_COLLAPSE]: {
    label: i18next.t('Collapse', {
      ns: 'highValueAdoption>>payments',
    }),
    trackingId: CommonActionTrackingIds.COLLAPSE_CARD,
  },
  [PlatformLocationFeatureV1.Types.Action.ACTION_EXPAND]: {
    label: i18next.t('Learn More', {
      ns: 'highValueAdoption>>payments',
    }),
    trackingId: CommonActionTrackingIds.EXPAND_CARD,
  },
};

export enum BulkCollectionsBannerType {
  NoPayments = 'no-payments',
  CollectionsFeatureDisabled = 'collections-feature-disabled',
  CollectionsFeatureEnabled = 'collections-feature-enabled',
}

export const BULK_COLLECTIONS_ARTICLE_LINK = 'https://www.weavehelp.com/weavehelp/article/payments-bulk-collections';

export const BulkCollectionsDescriptionTextMap = (pmsName?: string) => ({
  [BulkCollectionsBannerType.NoPayments]: i18next.t(
    'Activate Weave Payments to send payment requests for {{pmsName}} balances to multiple patients at once.',
    { ns: 'highValueAdoption>>payments', pmsName: pmsName || '[PMS]' }
  ),
  [BulkCollectionsBannerType.CollectionsFeatureDisabled]: i18next.t(
    'Enable Collections to send payment requests to multiple patients at once with Bulk Collections.',
    { ns: 'highValueAdoption>>payments' }
  ),
  [BulkCollectionsBannerType.CollectionsFeatureEnabled]: i18next.t(
    'Send payment requests to multiple patients at once with Bulk Collections.',
    { ns: 'highValueAdoption>>payments' }
  ),
});

export const getPmsDisplayName = (pmsKey: string): string => {
  const pmsType = findPmsTypeFromName(pmsKey);

  const pmsDisplayNameMap: Record<string, string> = {
    fuse: 'Fuse',
    eaglesoft: 'Eaglesoft',
    dentrixEnterprise: 'Dentrix Enterprise',
    openDental: 'OpenDental',
    openDentalCloud: 'OpenDental Cloud',
    openDentalLocal: 'OpenDental Local',
    drChrono: 'Dr. Chrono',
    dolphinCloud: 'Dolphin Cloud',
    dolphinLegacy: 'Dolphin Legacy',
    dolphin: 'Dolphin',
    crystal: 'Crystal',
    athenaOne: 'athenaOne',
    nextechPractice: 'Nextech Practice',
    nextechSelect: 'Nextech Select',
  };

  return pmsType ? pmsDisplayNameMap[pmsType] || '[PMS]' : '[PMS]';
};
